import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  approveAdminRegistration,
  editAdminRegistration,
  inactivateAdminRegistration,
  rejectAdminRegistration,
} from "@/api/admin";

const getErrorMessage = (error, fallback = "Request failed.") => {
  const statusCode = error?.response?.status;
  const message = error?.response?.data?.message || error?.message || fallback;

  if (statusCode === 429) {
    return "Too many requests. Please wait a moment and try again.";
  }

  return message;
};

export const useApproveRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => approveAdminRegistration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
      toast.success("Registration approved.");
    },
    onError: (error) => {
      toast.error("Approve failed", {
        description: getErrorMessage(error, "Failed to approve registration."),
      });
    },
  });
};

export const useRejectRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => rejectAdminRegistration({ id, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
      toast.success("Registration rejected.");
    },
    onError: (error) => {
      toast.error("Reject failed", {
        description: getErrorMessage(error, "Failed to reject registration."),
      });
    },
  });
};

export const useInactivateRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => inactivateAdminRegistration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
      toast.success("Registration moved to inactive.");
    },
    onError: (error) => {
      toast.error("Move to inactive failed", {
        description: getErrorMessage(error, "Failed to move registration to inactive."),
      });
    },
  });
};

export const useEditRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => editAdminRegistration({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "registrations"] });
      toast.success("Registration updated.");
    },
    onError: (error) => {
      toast.error("Update failed", {
        description: getErrorMessage(error, "Failed to update registration."),
      });
    },
  });
};
